import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { NewspaperOutlined } from '@mui/icons-material'
import NotificationsIcon from '@mui/icons-material/Notifications'
import { Paper, Slide } from '@mui/material'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChangesAlerts } from '../../../types'
import { goToNewsFromModel, goToRoom } from '../../actions/nav'
import { SettingsType } from '../../constants/enums'
import { authenticated } from '../../mfcLogic/auth'
import {
    AlertType,
    UEOPT_APP_ALERT_SETTINGS_TYPES,
} from '../../mfcLogic/ueopts'
import { DEFAULT_CONFIG, state } from '../../state/state.alerts'
import { idToSlug } from '../../state/state.users'
import i18n from '../../utilities/i18n'
import { useAlerts } from '../Hooks/useAlerts'
import { useSettingSelector } from '../Hooks/useSettings'
import APP_BREAKPOINTS, { minWindowWidth } from '../styles/breakpoints'
import { palette } from '../styles/colors'
import { PAPER_HEIGHTS } from '../theme'

const StyledTooltipPaper = styled(Paper)({
    display: 'flex',
    alignItems: 'baseline',
    background: palette.headerBackground,
    textAlign: 'center',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    maxWidth: 340,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    position: 'absolute',
    left: '148px',
    top: -30,
    flexFlow: 'row nowrap',
    '@media only screen and (min-width: 410px)': {
        left: 190,
    },
    '@media only screen and (max-width: 405px)': {
        left: 182,
    },
    '@media only screen and (max-width: 400px)': {
        left: 170,
    },
    '@media only screen and (max-width: 395px)': {
        left: 162,
    },
    '@media only screen and (max-width: 394px)': {
        left: 158,
    },
    '@media only screen and (max-width: 380px)': {
        left: 148,
    },
    '@media only screen and (max-width: 368px)': {
        left: 142,
    },
    '@media only screen and (min-width: 960px) and (orientation: portrait)': {
        left: 218,
    },
    '@media only screen and (min-width: 960px) and (orientation: landscape) ': {
        left: 272,
    },
    paddingTop: typeof window !== 'undefined' && window.isIOS ? '0px' : '1px',
    paddingRight: '6px',
    boxShadow: 'none',
    zIndex: PAPER_HEIGHTS.tip,
})

const fadeInStyles = css({
    top: 'unset',
    borderBottom: `2px solid ${palette.headerBackground}`,
    opacity: 1,
})

const StyledNotificationsIcon = styled(NotificationsIcon)({
    position: 'relative',
    top: '3px',
    height: '14px',
    marginRight: -2,
})

const StyledNewsIcon = styled(NewspaperOutlined)({
    position: 'relative',
    top: '3px',
    height: '14px',
    marginRight: -2,
})

const StyledName = styled('div')({
    fontSize: '11px',
    lineHeight: '11px',
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        lineHeight: '14px',
    },
    fontWeight: 'bold',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'inline-block',
})

const StyledStatus = styled('div')({
    fontSize: '10px',
    lineHeight: '10px',
    [minWindowWidth(APP_BREAKPOINTS.md)]: {
        lineHeight: '14px',
    },
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    display: 'inline-block',
})

export interface IAlertsState {
    canDisplayNext: boolean
    current: ChangesAlerts
    next: ChangesAlerts
    display: boolean
}

const AlertTooltips = (props: { addLeftMargin?: boolean }) => {
    const addLeftMargin = props.addLeftMargin
    const { alerts: newAlerts } = useAlerts({
        config: DEFAULT_CONFIG,
        updatesOnly: true,
    })
    const isAuthenticated = authenticated()
    const [alertsSettings] = useSettingSelector(SettingsType.DEFAULT, 'alerts')
    const showNoAlerts =
        Number(alertsSettings) ===
        UEOPT_APP_ALERT_SETTINGS_TYPES[AlertType.OFF].bit
    const showNewsAlertsOnly =
        Number(alertsSettings) ===
        UEOPT_APP_ALERT_SETTINGS_TYPES[AlertType.NEWS].bit
    const showStatusAlertsOnly =
        Number(alertsSettings) ===
        UEOPT_APP_ALERT_SETTINGS_TYPES[AlertType.STATUS].bit

    const [alert, setAlertState] = useState({
        display: false,
        current: {},
        next: null,
        //
        // Useful for allowing the animation to fade with the current information
        //
        canDisplayNext: true,
    } as IAlertsState)
    const hideTimeout = useRef(null)

    //
    // Ensure that the entries are unique and only the lastest alert for a
    // given model will canDisplayNext
    //
    const queueMap = useMemo(() => new Map<number, ChangesAlerts>(), [])

    const setAlert = (update: Partial<IAlertsState>) => {
        setAlertState({ ...alert, ...update })
    }
    const { current, display, canDisplayNext, next } = alert
    const { name, key, id, news } = current

    const resolveNextItem = (current) => {
        if (queueMap.size === 0) {
            setAlert({ display: false, next: null, current })
            return
        }

        const alerts = Array.from(queueMap.values())
        setAlert({ next: alerts[alerts.length - 1], display: false, current })
    }

    useEffect(() => {
        return () => {
            clearTimeout(hideTimeout.current)
        }
    }, [])

    useEffect(() => {
        if (newAlerts.length === 0) {
            return
        }

        const previouslyEmpty = queueMap.size === 0

        newAlerts.forEach((alert) => {
            const { id } = alert
            queueMap.set(id, alert)
        })

        if (!previouslyEmpty) {
            return
        }

        const alerts = Array.from(queueMap.values())

        setAlert({ next: alerts[alerts.length - 1] })
    }, [newAlerts])

    useEffect(() => {
        if (alert.display || !alert.canDisplayNext || !alert.next) {
            return
        }

        const { next } = alert
        const { id } = alert.next

        queueMap.delete(id)

        setAlert({
            canDisplayNext: false,
            display: true,
            current: next,
        })

        hideTimeout.current = setTimeout(() => resolveNextItem(next), 3000)
    }, [display, canDisplayNext, next])

    const onClickAndDismissAlert = useCallback(() => {
        const slug = idToSlug[id]

        clearTimeout(hideTimeout.current)
        if (news) goToNewsFromModel(slug)
        else goToRoom(idToSlug[id])
        resolveNextItem(current)
    }, [id, news])

    if (showNoAlerts || !isAuthenticated) {
        return null
    }
    if (state.alerts.size === 0) {
        return <></>
    }
    if (news && showStatusAlertsOnly) {
        return null
    }
    if (!news && showNewsAlertsOnly) return null

    const Icon = news ? StyledNewsIcon : StyledNotificationsIcon

    return (
        <Slide
            addEndListener={() => {
                setAlert({ canDisplayNext: true })
            }}
            in={alert.display}
            direction="down"
        >
            <StyledTooltipPaper
                data-testid="Alert__Tooltips"
                css={[
                    fadeInStyles,
                    ...(addLeftMargin ? [{ marginLeft: 58 }] : []),
                ]}
                key={id}
                onClick={onClickAndDismissAlert}
            >
                <Icon />
                <StyledName>{name}</StyledName>
                <StyledStatus>
                    &nbsp;
                    {news
                        ? i18n.t(`alerts.newsUpdate`)
                        : i18n.t(`alerts.${key}`)}
                </StyledStatus>
            </StyledTooltipPaper>
        </Slide>
    )
}

export default memo(AlertTooltips)
